import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { catchError, delay, forkJoin, of, Subscription, switchMap, tap } from 'rxjs';
import { HttpErrorResponse } from '@angular/common/http';
import { TranslateService } from '@ngx-translate/core';
import { LoaderStatus } from 'libs/core/src/lib/model/loading/loading-status.enum';
import { BasketPageModel } from 'libs/core/src/lib/model/page/basket/basket.page.model';
import { PersonalComponent } from '../../component/personal/personal.component';
import { ResponseValidatorService } from 'libs/core/src/lib/service/validator/response-validator.service';
import { BasketDataProvider } from 'libs/core/src/lib/data-provider/basket.data-provider';
import { OrderDataProvider } from 'libs/core/src/lib/data-provider/order.data-provider';
import { LoginState } from 'libs/core/src/lib/enum/state';
import { MessageModel, MessageType } from 'libs/core/src/lib/model/message.model';
import { PersonalModel } from 'libs/core/src/lib/model/personal.model';
import { OrderViewModel } from 'libs/core/src/lib/model/view-model/order/order.view.model';
import { CountdownComponentService } from 'libs/core/src/lib/service/countdown.service';
import { MessageService } from 'libs/core/src/lib/service/message.service';
import { NavigationHelperService } from 'libs/core/src/lib/service/navigation/navigation-helper.service';
import { AuthStateService } from 'libs/core/src/lib/state/auth.state.service';
import { OrderStateService } from 'libs/core/src/lib/state/order.state.service';
import { GoogleTagManagerService } from 'libs/core/src/lib/service/analytics-services/google-tag-manager.service';
import { UserDataProvider } from 'libs/core/src/lib/data-provider/user.data-provider';
import { UserApiModel } from 'libs/core/src/lib/model/api-model/user/user.api.model';
import { LoaderService } from 'libs/core/src/lib/service/loading.service';
import { LoaderEnum } from 'libs/core/src/lib/enum/loader.enum';
import { FlowService } from 'libs/core/src/lib/service/flow.service';
import { Location } from '@angular/common';
import { NavigationService } from 'libs/core/src/lib/service/navigation/navigation.service';

@Component({
  template: ''
})
export class PersonalPageComponent implements OnInit, OnDestroy {
  loaderEnum: typeof LoaderEnum = LoaderEnum;
  private orderStateSubscription: Subscription;
  public loaderStatus = LoaderStatus.pending;
  public formErrors: Object = null;
  public basketPageModel: BasketPageModel = null;

  public canUpdateOrderAgreements = true;
  public order: OrderViewModel;

  @ViewChild('personalComponent') public personalComponent: PersonalComponent;

  constructor(
    protected responseValidator: ResponseValidatorService,
    protected orderDataProvider: OrderDataProvider,
    protected orderStateService: OrderStateService,
    protected navigationHelper: NavigationHelperService,
    protected router: Router,
    protected route: ActivatedRoute,
    protected countdownComponentService: CountdownComponentService,
    protected messageService: MessageService,
    protected translate: TranslateService,
    protected authStateService: AuthStateService,
    protected basketDataProvider: BasketDataProvider,
    protected googleTagManagerService: GoogleTagManagerService,
    protected userDataProvider: UserDataProvider,
    protected loaderService: LoaderService,
    protected flowService: FlowService,
    protected location: Location,
    protected navigationService: NavigationService
  ) {}

  ngOnInit() {
    const random = Math.random();
    this.showLoader(random);

    this.countdownComponentService.start();

    this.orderStateSubscription = this.orderStateService.state$
      .pipe(
        switchMap((order) => {
          // Transform data here
          this.order = order;
          return this.basketDataProvider.updateBasket(order).pipe(
            catchError(() => {
              this.navigationService.goTo(['error', '20001']);
              return of(null);
            })
          );
        })
      )
      .subscribe((basketPageModel) => {
        this.basketPageModel = basketPageModel;
        this.hideLoader(random);
      });

    this.googleTagManagerService.addToCart();
  }

  showLoader(processKey?: number) {
    if (processKey) {
      this.processKey = processKey;
    }

    if (this.loaderStatus === LoaderStatus.pending) {
      return;
    }

    this.loaderStatus = LoaderStatus.pending;
    this.loaderService.showLoader(LoaderEnum.MAIN);
  }

  hideLoader(processKey?: number) {
    if (this.processKey && this.processKey != processKey) {
      return;
    }

    this.processKey = undefined;
    this.loaderStatus = LoaderStatus.stopped;
    this.loaderService.hideLoader(LoaderEnum.MAIN);
    this.loaderService.hideLoader(LoaderEnum.NEXT_BUTTON);
  }

  processKey: number = 0;
  onUserFormSubmit(value: PersonalModel) {
    const loaderKey = Math.random();
    this.showLoader(loaderKey);

    const orderRequest = Object.assign(this.order.toApiModel(), {
      userEmail: value.email,
      userFirstName: value.firstName,
      userLastName: value.lastName,
      userPhone: value.phone,
      taxId: value.taxId
    });

    this.orderDataProvider
      .put(this.order.cinemaId, orderRequest)
      .pipe(
        delay(0),
        switchMap((order: OrderViewModel) => {
          this.orderStateService.setOrder(order);

          this.canUpdateOrderAgreements = false;
          const checkedAgreements = this.getBasketAgreements(true);

          return forkJoin({
            orderAgreements: this.putOrderAgreements(checkedAgreements),
            userAgreements: this.putUserAgreements(value, checkedAgreements),
            customAction: this.doCustomActions(value, checkedAgreements)
          });
        })
      )
      .subscribe({
        next: () => {
          this.goNextRoute();
          this.hideLoader(loaderKey);
        },
        error: (errors: HttpErrorResponse) => {
          this.hideLoader(loaderKey);
          this.formErrors = this.responseValidator.handleProperiesErrors(errors);
        }
      });
  }

  onLoginFormEventEmit(state: LoginState) {
    this.messageService.clear();

    if (state === LoginState.onRegister) {
      this.goToLoginFormRegister();
    }

    if (state === LoginState.LogInOK) {
      this.orderDataProvider.put(this.order.cinemaId, this.order.toApiModel()).subscribe(() => {
        this.goNextRoute();
      });
    }

    if (state === LoginState.LogInFault) {
      this.onLoginError();
    }
  }

  goToLoginFormRegister() {
    this.navigationService.goTo(['user-card-register']);
  }

  goNextRoute() {
    if (this.order.valueToPay === 0) {
      this.showLoader();
      this.orderDataProvider.closeBasket(this.order.cinemaId, this.order.id).subscribe({
        complete: () => {
          const nextRoute = this.navigationHelper.getNextRouteFor(this.route.snapshot.data.pageIdentify);
          const finalRoute = this.navigationHelper.getNextRouteFor(nextRoute);
          this.navigationService.goTo([finalRoute], {
            queryParams: {
              orderId: this.order.id,
              cinemaId: this.order.cinemaId
            }
          });
        }
      });
    } else {
      this.navigationService.goTo([this.navigationHelper.getNextRoute(this.route.snapshot)]);
    }
  }

  onLoginError() {
    this.messageService.add(new MessageModel(MessageType.warning, this.translate.instant('errors.10008'), '10008'));
  }

  ngOnDestroy(): void {
    this.orderStateSubscription?.unsubscribe();
  }

  public onStepBackClick(): void {
    window.history.back();
  }

  public onNavigationClick(event: string) {
    switch (event) {
      case 'previous':
        this.navigationService.goTo([this.navigationHelper.getPreviousRoute(this.route.snapshot)]);
        break;
      case 'next':
        if (this.canUpdateOrderAgreements) {
          this.putOrderAgreements(this.getBasketAgreements(true));
        }

        if (this.personalComponent) {
          this.personalComponent.onSubmit();
        } else {
          this.navigationService.goTo([this.navigationHelper.getNextRoute(this.route.snapshot)]);
        }

        break;
    }
  }

  protected getBasketAgreements(checked: boolean | null = null): string[] {
    return (
      this.basketPageModel.agreements
        ?.filter((agreement) => checked === null || agreement?.checked === checked)
        .map((agreement) => agreement.id.toUpperCase()) ?? []
    );
  }

  get isLoaded() {
    return this.loaderStatus === LoaderStatus.stopped;
  }

  protected putOrderAgreements(checkedAgreements: string[]) {
    return this.orderDataProvider.updateAgreements(this.order.cinemaId, this.order.id, checkedAgreements);
  }

  protected putUserAgreements(value: PersonalModel, checkedAgreements: string[]) {
    const token = this.authStateService.getToken();
    if (!token) {
      return of(null);
    }

    const user = Object.assign(new UserApiModel(), {
      ...this.authStateService.getUser(),
      phone: value.phone,
      token
    });

    user.agreements = [...user.agreements.filter((agreement) => !checkedAgreements.includes(agreement.toUpperCase())), ...checkedAgreements];

    if (user.agreements.length) {
      user.agreements = [...checkedAgreements];

      return this.userDataProvider.update(user, user.token).pipe(tap((user) => this.authStateService.setUser(user.toApiModel())));
    }

    return of(null);
  }

  protected doCustomActions(value: PersonalModel, checkedAgreements: string[]) {
    return of(null);
  }

  goToPreviousRoute(): void {
    this.navigationService.goTo([this.navigationHelper.getPreviousRoute(this.route.snapshot)]);
  }

  canShowSubmitButton() {
    return this.authStateService.userIsLoggedAndTokenIsValid() || this.personalComponent?.personalForm?.value?.autoRegisterAccount !== null;
  }
}
